import type { Batch } from '@ctcells/contract'
import { devQuickBatchConfig, safeStorage } from '@ctcells/core'
import { t } from '@ctcells/i18n'

/**
 * 작업을 **사람에게 부르는 이름** — 한 곳(eric 2026-09-28 「배치 UUID 아이디가 길게 나오는데..
 * 일단 테스트 배치 이름은 eric 테스트 라고 배치 이름 하드 코딩해줘. 나중에 백엔드 요청할거야」 →
 * eric 2026-09-29 「테스트 작업 돌릴 때는.. 테스트 작업 이라고 이름 붙여줘.. 다른 거 쓰지마..」).
 *
 * 순서
 * 1. **「테스트 작업 시작」 버튼이 만든 작업**이면 「테스트 작업」(`dev.quickBatchName`) — 임시 · 개발용
 * 2. 서버 `Batch.name`(서버가 「레시피 · 수행인 · 시각」으로 조립한다 · 사람이 적은 이름은 아직 못 받는다 — 요청 93)
 * 3. 없으면 `batch_id`
 *
 * 🔴 **1번의 판정은 서버 응답의 `recipe_id` 로 한다** — 버튼은 늘 설정 레시피(`VITE_DEV_QUICK_BATCH_RECIPE`)로
 *    작업을 만든다. 그래서 `Batch.recipe_id === 설정 레시피` 면 버튼 작업이다. 이름 자리에 서버 이름 세 칸
 *    (「레시피 · 수행인 · 시각」)을 잇지 않고 「테스트 작업」 한 칸만 세운다.
 *    ⚠ 2026-09-29 까지는 이 브라우저의 기억(`rememberQuickBatch`) 하나로 갈랐다 — **다른 브라우저 · 다른
 *      계정이 누른 작업**(개발 도구 계정 러너)은 서버 이름 세 칸이 통째로 이름 자리에 섰다(eric 캡처).
 *      기억은 설정 레시피를 바꾸기 전의 작업을 위해 남긴다.
 *    ⚠ 서버 이름에 「테스트 작업」을 싣는 길은 아직 없다 — 작업 이름 칸을 서버가 받지 않고(요청 93), 레시피
 *      개명도 막혀 있다(`devQuickBatch.QUICK_BATCH_NAME` 머리말). 그래서 이 표시가 임시로 선다.
 *    ⚠ 같은 레시피로 「새 작업」을 사람이 만들어도 「테스트 작업」으로 보인다 — 그 레시피가 곧 시험 레시피라서다.
 * 🔴 **`VITE_DEV_QUICK_BATCH` 가 꺼져 있으면 1번은 없다** — 고객 화면에서는 서버 `name` 그대로다.
 * ⚠ 이름은 **표시**다. 판정 · 링크 · 키에는 계속 `batch_id` 를 쓴다.
 */

const STORAGE_KEY = 'ctcells.dev.quickBatches.v1'
/** 오래 켜 둔 단말에서 무한히 늘지 않게. 넘치면 오래된 것부터 버린다 */
const MAX_QUICK_BATCHES = 50

function load(): string[] {
  const raw = safeStorage.readJson<unknown>(STORAGE_KEY)
  return Array.isArray(raw) ? raw.filter((v): v is string => typeof v === 'string') : []
}

/** 「테스트 작업 시작」 버튼이 서버 응답의 `batch_id` 로 부른다 */
export function rememberQuickBatch(batchId: string): void {
  const next = [...load().filter((id) => id !== batchId), batchId].slice(-MAX_QUICK_BATCHES)
  safeStorage.writeJson(STORAGE_KEY, next)
}

/** 버튼이 만든 작업인가 — 서버 `recipe_id` 가 설정 레시피이거나, 이 브라우저가 기억한 작업 */
export function isQuickBatch(batchId: string, recipeId?: string | null): boolean {
  const config = devQuickBatchConfig()
  if (!config.enabled) return false
  return (config.recipeId !== undefined && recipeId === config.recipeId) || load().includes(batchId)
}

export function batchDisplayName(
  batch: Pick<Batch, 'batch_id'> & { name?: string | null; recipe_id?: string | null },
): string {
  if (isQuickBatch(batch.batch_id, batch.recipe_id)) return t('dev.quickBatchName')
  const name = batch.name?.trim()
  return name === undefined || name === '' ? batch.batch_id : name
}
